/**
 * Platform-free rules for the "jump to latest" control on bottom-anchored
 * streams (chat threads, QuantisAI). The web hook and the RN screens share
 * these so the threshold, the unseen count and the copy cannot drift.
 */

/** Matches the chat window's long-standing 150px near-bottom band. */
export const JUMP_TO_LATEST_THRESHOLD = 150;

export interface ScrollMetrics {
  /** Distance scrolled from the top (scrollTop / contentOffset.y). */
  offset: number;
  /** Visible height (clientHeight / layoutMeasurement.height). */
  viewport: number;
  /** Total scrollable height (scrollHeight / contentSize.height). */
  content: number;
  /** Extra scrollable space below the content, such as an RN contentInset. */
  insetBottom?: number;
}

export function distanceFromBottom({ offset, viewport, content, insetBottom = 0 }: ScrollMetrics): number {
  return Math.max(0, content + insetBottom - viewport - offset);
}

export function isNearBottom(metrics: ScrollMetrics, threshold = JUMP_TO_LATEST_THRESHOLD): boolean {
  return distanceFromBottom(metrics) < threshold;
}

/**
 * Items that arrived while the reader was scrolled up add to the count; a
 * reader at the bottom sees them land, so nothing is unseen. A shrinking list
 * (a deletion or a scope swap the caller did not report) leaves it alone.
 */
export function nextUnseenCount(
  unseen: number,
  { previousCount, nextCount, nearBottom }: { previousCount: number; nextCount: number; nearBottom: boolean },
): number {
  if (nearBottom) return 0;
  const added = nextCount - previousCount;
  return added > 0 ? unseen + added : unseen;
}

/** The visible pill text: the count when there is one, otherwise "Latest". */
export function jumpToLatestText(unseen: number): string {
  return unseen > 0 ? String(unseen) : 'Latest';
}

export function jumpToLatestAccessibleLabel(unseen: number, noun: { one: string; many: string } = { one: 'message', many: 'messages' }): string {
  return unseen > 0
    ? `Jump to latest, ${unseen} new ${unseen === 1 ? noun.one : noun.many}`
    : `Jump to latest ${noun.one}`;
}
